/**
 * Leave approval sidebar badge store.
 *
 * Written by `<LeaveApprovalBadgeSubscriber />`; read synchronously by the
 * "Leave Approvals" and "Final Approvals" menu items in `membersMenu.tsx`
 * (both their `visible()` checks and their `countSelector`s).
 *
 * - The baseline is `LeaveApprovalService.getCapabilities()` (REST), refreshed
 *   on mount, on focus, every 5 minutes and on hub reconnect.
 * - The `leaveapprovals` hub pushes SIGNED deltas (+1 / -1 per leave) for
 *   `myPendingApprovals` and `finalApprovalQueue`; `applyDelta` reduces them
 *   on top of the baseline.
 *
 * The flags are stored as 0/1 numbers (not booleans) so the menu revision hook
 * can sum them with the counters. `revision` ticks on every write, so the menu
 * re-renders even when a write leaves the sum of the counters unchanged.
 */

import { create } from 'zustand';
import type { LeaveApproverCapabilities } from '@actschurch/ccms-sdk';

/** Signed per-user changes from the hub (the store's names, already mapped). */
export interface LeaveApprovalBadgeDeltas {
  myPendingApprovals?: number;
  finalQueue?: number;
}

interface LeaveApprovalBadgesState {
  /** Line steps Pending on the user (the "Leave Approvals" badge). */
  myPendingApprovals: number;
  /** HR final steps Pending that the user may act on (the "Final Approvals" badge). */
  finalQueue: number;
  /** 1 when the user is a line approver (named on a rule, a head with a head rule, or has approval work). */
  isLineApprover: number;
  /** 1 when the user holds HRAdmin. */
  isFinalApprover: number;
  /** 1 when the user is the only active HRAdmin. */
  isSoleFinalApprover: number;
  /** Line steps the user sent back to the employee (the "Waiting on employee" tab). */
  myWaitingOnEmployee: number;
  /** True once the first capabilities baseline has landed. */
  loaded: boolean;
  /** Ticks on every write; a change signal for the menu renderer. */
  revision: number;
  setBaseline: (caps: LeaveApproverCapabilities | null | undefined) => void;
  applyDelta: (deltas: LeaveApprovalBadgeDeltas | null | undefined) => void;
  reset: () => void;
}

const EMPTY = {
  myPendingApprovals: 0,
  finalQueue: 0,
  isLineApprover: 0,
  isFinalApprover: 0,
  isSoleFinalApprover: 0,
  myWaitingOnEmployee: 0,
  loaded: false,
};

// Counters clamp at zero so a stray -1 arriving before the baseline cannot push a badge negative.
const clamp = (n: number) => (n < 0 ? 0 : n);
const flag = (b: boolean | null | undefined) => (b ? 1 : 0);

export const useLeaveApprovalBadgesStore = create<LeaveApprovalBadgesState>((set) => ({
  ...EMPTY,
  revision: 0,
  setBaseline: (caps) =>
    set((state) => ({
      myPendingApprovals: clamp(caps?.myPendingCount ?? 0),
      finalQueue: clamp(caps?.finalQueueActionableCount ?? 0),
      isLineApprover: flag(caps?.isLineApprover),
      isFinalApprover: flag(caps?.isFinalApprover),
      isSoleFinalApprover: flag(caps?.isSoleFinalApprover),
      myWaitingOnEmployee: clamp(caps?.myWaitingOnEmployeeCount ?? 0),
      loaded: true,
      revision: state.revision + 1,
    })),
  applyDelta: (deltas) =>
    set((state) => ({
      myPendingApprovals: clamp(state.myPendingApprovals + (deltas?.myPendingApprovals ?? 0)),
      finalQueue: clamp(state.finalQueue + (deltas?.finalQueue ?? 0)),
      revision: state.revision + 1,
    })),
  reset: () => set((state) => ({ ...EMPTY, revision: state.revision + 1 })),
}));

/** Sync read for menu `visible()` checks: may this user see the "Leave Approvals" page? */
export function canSeeLeaveApprovalsMenu(): boolean {
  const s = useLeaveApprovalBadgesStore.getState();
  return s.isLineApprover === 1 || s.myPendingApprovals > 0 || s.myWaitingOnEmployee > 0;
}
